import React, { useRef, useState, useEffect } from 'react';
import { notification } from 'antd';
import { ICurrentUser } from "../types/user";
import { NOTIFICATION_TIME } from "../utils/cookieUtils";
import Api from "../api/Api";

interface SessionManagerReturn {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  loadingSession: boolean;
  handleLogin: () => void;
}

export function useSessionManager(user: ICurrentUser): SessionManagerReturn {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [loadingSession, setLoadingSession] = useState<boolean>(false);

  const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const periodicCheckRef = useRef<NodeJS.Timeout | null>(null);
  const activityLockRef = useRef<boolean>(false);
  const isAuthenticated = !!user?.authenticated;

  let eventCleanup: (() => void) | null = null;

  /**
   * Clean up all timers and event listeners
   */
  const cleanUpAllMonitoring = (): void => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
      notificationTimerRef.current = null;
    }

    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }

    if (checkIntervalRef.current) {
      clearTimeout(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    if (periodicCheckRef.current) {
      clearInterval(periodicCheckRef.current);
      periodicCheckRef.current = null;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (eventCleanup) {
      eventCleanup();
      eventCleanup = null;
    }
  };

  /**
   * Handle session expiration by showing the modal and stopping all monitoring
   */
  const handleSessionExpired = (): void => {
    setIsModalOpen(true);
    cleanUpAllMonitoring();
  };

  /**
   * Scheduled expiration time
   *
   * @param expirationTime - Expired time in millisecond
   */
  const scheduleExpirationNotification = (expirationTime: number): void => {
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;

    if (timeUntilExpiration > 0) {
      expirationTimerRef.current = setTimeout(() => {
        handleSessionExpired();
      }, timeUntilExpiration);
    } else {
      handleSessionExpired();
    }
  };

  /**
   * Scheduling notification before is ended
   *
   * @param expiration - Expired time in millisecond
   */
  const scheduleNotification = (expiration: number): void => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }

    const now = Date.now();
    const notifyTime = expiration - NOTIFICATION_TIME;

    if (notifyTime > now) {
      notificationTimerRef.current = setTimeout(() => {
        notification.warning({
          message: 'Your session is about to expire',
          description: (
            <>
              It looks like you&apos;ve been inactive for a while.<br/>
              For security reasons, your session will expire soon.
              If you&apos;re still here, simply take action to stay logged in.
            </>
          ),
          duration: null,
        });
      }, notifyTime - now);
    }
  };

  /**
   * Get expired time from server
   *
   * @returns Expired time in millisecond or false if failed
   */
  const fetchSession = async (): Promise<number | false> => {
    try {
      if(loadingSession) {
        return false;
      }

      setLoadingSession(true);
      const res = await Api.auth.getExpirationDate();
      const newExpiration = new Date(res as string).getTime();

      const now = Date.now();
      if (newExpiration <= now) {
        handleSessionExpired();
      }

      return newExpiration;
    } catch (e) {
      return false;
    } finally {
      setLoadingSession(false);
    }
  };

  const keepSessionAlive = async (): Promise<boolean> => {
    const res = await Api.auth.keepSessionActive();

    return !!res;
  }

  /**
   * Start periodic session check at 1-minute intervals
   * @param currentExpiration - Current known expiration time
   */
  const startPeriodicCheck = (currentExpiration: number): void => {
    if (periodicCheckRef.current) {
      clearInterval(periodicCheckRef.current);
    }

    periodicCheckRef.current = setInterval(async () => {
      try {
        const newExpiration = await fetchSession();

        if (newExpiration) {
          const now = Date.now();
          if (newExpiration <= now) {
            handleSessionExpired();
            return;
          }

          if (newExpiration !== currentExpiration) {
            if (notificationTimerRef.current) {
              clearTimeout(notificationTimerRef.current);
            }

            if (checkIntervalRef.current) {
              clearTimeout(checkIntervalRef.current);
            }

            if (expirationTimerRef.current) {
              clearTimeout(expirationTimerRef.current);
            }

            if (periodicCheckRef.current) {
              clearInterval(periodicCheckRef.current);
              periodicCheckRef.current = null;
            }

            if (eventCleanup) {
              eventCleanup();
              eventCleanup = null;
            }

            // Schedule new timers and monitoring
            scheduleNotification(newExpiration);
            scheduleExpirationNotification(newExpiration);
            startMonitoring(newExpiration);
          }
        }
      } catch (e) {
        console.log('Failed to get new expiration', e)
      }
    }, 60000);
  };

  /**
   * Controling monitoring activity
   *
   * @param expiration - Expired time in millisecond
   * @returns Cleanup event listener
   */
  const setupActivityMonitoring = (expiration: number): (() => void) => {
    if (eventCleanup) {
      eventCleanup();
      eventCleanup = null;
    }

    const handleActivityAfterHalfway = async (): Promise<void> => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        if (activityLockRef.current) {
          return;
        }

        activityLockRef.current = true;

        const now = Date.now() + 60000;
        const timeUntilExpiration = expiration - now;
        const halfwayPoint = now + (timeUntilExpiration / 2);

        if (timeUntilExpiration <= 0) {
          handleSessionExpired();
          return;
        }

        if (timeUntilExpiration <= halfwayPoint && timeUntilExpiration > 0 && !loadingSession) {
          try {
            const newExpiration = await fetchSession();

            if (newExpiration && newExpiration !== expiration) {
              if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
              }

              if (checkIntervalRef.current) {
                clearTimeout(checkIntervalRef.current);
              }

              if (expirationTimerRef.current) {
                clearTimeout(expirationTimerRef.current);
              }

              if (periodicCheckRef.current) {
                clearInterval(periodicCheckRef.current);
                periodicCheckRef.current = null;
              }

              scheduleNotification(newExpiration);
              scheduleExpirationNotification(newExpiration);

              if (eventCleanup) {
                eventCleanup();
                eventCleanup = null;
              }

              startMonitoring(newExpiration);
            } else {
              startPeriodicCheck(expiration);
            }
          } finally {
            activityLockRef.current = false;
          }
        } else {
          activityLockRef.current = false;
        }
      }, 500);
    };

    const events = ['click', 'keypress', 'mousemove'];
    events.forEach(e => window.addEventListener(e, handleActivityAfterHalfway));

    eventCleanup = () => {
      events.forEach(e => window.removeEventListener(e, handleActivityAfterHalfway));
    };

    return eventCleanup;
  };

  /**
   * Start monitoring activity
   *
   * @param initialExpiration - Initial expiration in millisecond
   */
  const startMonitoring = (initialExpiration: number): void => {
    const now = Date.now();
    if (initialExpiration <= now) {
      handleSessionExpired();
      return;
    }

    const duration = initialExpiration - now;
    const halfwayPoint = now + (duration / 2);
    const initialDelay = Math.max(0, halfwayPoint - now);

    checkIntervalRef.current = setTimeout(() => {
      setupActivityMonitoring(initialExpiration);
    }, initialDelay);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const runPeriodically = async () => {
      try {
        await keepSessionAlive();
      } catch (error) {
        console.error("Error keeping session alive:", error);
      }
    };

    runPeriodically();

    const interval = setInterval(runPeriodically, 300000);

    return () => {
      clearInterval(interval);
    };
  },[isAuthenticated, keepSessionAlive]);

  const handleLogin = (): void => {
    (window.top || window).location.assign(
      `${Api.SERVER_DOMAIN}${Api.IISSite}/auth/signin`
    );
  };

  return {
    isModalOpen,
    setIsModalOpen,
    loadingSession,
    handleLogin
  };
}
